import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { DataService } from '../data.service';
import { KozosCegService } from '../kozos-ceg.service';

/*  GÉPJÁRMŰ – az aktív cég (a Könyvelés ügyfele vagy a modul saját ügyfele), a szerep rajta és az év.
 *
 *  SZEREP: 'ugyfel' – a felhasználó a cég nevében dolgozik; 'konyvelo' – a könyvelőiroda munkatársa. A
 *  Gépjárműben mindkettő ír (a kiszolgáló dönti el: gepjarmu-kozos.js jog). A cég a többi modullal közös
 *  (KozosCegService): a Könyvelésből átlépve ugyanaz a cég nyílik. */
export interface GepjarmuAktiv {
  intezmeny: string;
  ugyfelId: string | null;
  ugyfelNev: string | null;
  szerep: 'ugyfel' | 'konyvelo' | null;
  ev: number;
}

@Injectable({ providedIn: 'root' })
export class GepjarmuService {
  aktiv$ = new BehaviorSubject<GepjarmuAktiv>({ intezmeny: '', ugyfelId: null, ugyfelNev: null, szerep: null, ev: new Date().getFullYear() });
  get aktiv(): GepjarmuAktiv { return this.aktiv$.value; }
  get ir(): boolean { return !!this.aktiv.szerep; }
  private kesz = false;

  constructor(private http: HttpClient, private data: DataService, private kozos: KozosCegService) {}

  async init(): Promise<GepjarmuAktiv> {
    if (this.kesz) return this.aktiv;
    const si = await this.data.getFromStorage('sajatIntezmeny');
    const m = (await this.data.getFromStorage('gepjarmuAktiv')) || {};
    this.aktiv$.next({ intezmeny: si?.intezmenyId || '', ugyfelId: m.ugyfelId || null, ugyfelNev: m.ugyfelNev || null,
      szerep: m.szerep || null, ev: m.ev || new Date().getFullYear() });
    this.kesz = true;
    return this.aktiv;
  }
  async setAktiv(patch: Partial<GepjarmuAktiv>) {
    const a = { ...this.aktiv, ...patch };
    this.aktiv$.next(a);
    await this.data.setStorage('gepjarmuAktiv', { ugyfelId: a.ugyfelId, ugyfelNev: a.ugyfelNev, szerep: a.szerep, ev: a.ev });
    if (a.ugyfelId) await this.kozos.jelol('GEPJARMU', a.ugyfelId);
  }
  /** A látható cégek (szereppel) – az ügyfél csak a sajátjait, a könyvelő mindet. */
  ugyfelek(): Promise<any[]> { return this.get(`/ugyfelek/${this.aktiv.intezmeny}`); }
  /*  Modulba lépéskor: a másik modulban épp aktív cég (ha itt is látható), különben a legutóbbi;
   *  ha az sem látható már (pl. elvett kötés), az első látható cég. A szerep mindig frissül. */
  async kozosAtvesz(): Promise<void> {
    let lista: any[] = [];
    try { lista = await this.ugyfelek(); } catch { return; }
    const atv = await this.kozos.atveendo('GEPJARMU');
    const u = (atv && lista.find((x) => x.id === atv)) || lista.find((x) => x.id === this.aktiv.ugyfelId)
      || lista.find((x) => x.szerep === 'ugyfel' && !x.inaktiv) || (lista.length === 1 ? lista[0] : null);
    if (u) await this.setAktiv({ ugyfelId: u.id, ugyfelNev: u.nev, szerep: u.szerep });
    else if (this.aktiv.ugyfelId) await this.setAktiv({ ugyfelId: null, ugyfelNev: null, szerep: null });
  }

  /*  Három kiszolgálói rész: /api/gepjarmu (törzsek, költségek), /api/gepjarmu-ut (utak, elszámolás,
   *  kiküldetés), /api/gepjarmu-ado (cégautóadó). Az `ut`/`ado` előtagú útvonal a megfelelőre megy:
   *  get('ut:/utak/…'), get('ado:/szamfejtes/…'). */
  private url(p: string) {
    if (p.startsWith('ut:')) return `/api/gepjarmu-ut${p.slice(3)}`;
    if (p.startsWith('ado:')) return `/api/gepjarmu-ado${p.slice(4)}`;
    return `/api/gepjarmu${p}`;
  }
  get<T = any>(p: string): Promise<T> { return firstValueFrom(this.http.get<T>(this.url(p))); }
  post<T = any>(p: string, body: any = {}): Promise<T> { return firstValueFrom(this.http.post<T>(this.url(p), body)); }
  /** Multipart (fájlfeltöltés, pl. forgalmi-fotó OCR) – a böngésző állítja be a Content-Type határt. */
  postForm<T = any>(p: string, fd: FormData): Promise<T> { return firstValueFrom(this.http.post<T>(this.url(p), fd)); }
  put<T = any>(p: string, body: any = {}): Promise<T> { return firstValueFrom(this.http.put<T>(this.url(p), body)); }
  del<T = any>(p: string): Promise<T> { return firstValueFrom(this.http.delete<T>(this.url(p))); }
  megnyit(p: string, cim = 'Dokumentum') { return this.data.megnyitFajl(this.url(p), cim); }
  /** POST-tal kért fájl (címke-PDF, export) – a tokennel töltjük le, és új lapon nyitjuk / mentjük. */
  async postFajl(p: string, body: any, fajlnev: string, letolt = false) {
    const lap = letolt ? null : window.open('', '_blank');
    const blob = await firstValueFrom(this.http.post(this.url(p), body, { responseType: 'blob' }));
    const url = URL.createObjectURL(blob);
    if (lap) lap.location.href = url;
    else { const a = document.createElement('a'); a.href = url; a.download = fajlnev; a.click(); }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
  async getFajl(p: string, fajlnev: string) {
    const blob = await firstValueFrom(this.http.get(this.url(p), { responseType: 'blob' }));
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = fajlnev; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
}
